/**
 * Blossom — product.js
 * Product page components (also used by quick add):
 *   <product-gallery>        scroll-snap media viewer with thumbnails, dots,
 *                            counter, keyboard support, variant media sync and
 *                            zoom lightbox (snippets/product-media-gallery.liquid)
 *   <product-deferred-media> videos / 3D models that load only when played
 *   <sticky-add-to-cart>     mobile add-to-bag bar shown when the main button
 *                            leaves the viewport
 *   <share-button>           native share sheet with copy-link fallback
 */
(() => {
  if (!window.Theme) return;

  /* ---------------------------------------------------- <product-gallery> */
  if (!customElements.get('product-gallery')) {
    class ProductGallery extends HTMLElement {
      connectedCallback() {
        if (this.initialized) return;
        this.track = this.querySelector('[data-gallery-track]');
        if (!this.track) return;
        this.initialized = true;
        this.sectionId = this.dataset.sectionId;
        this.activeIndex = Number(this.dataset.initialIndex || 0);

        this.querySelectorAll('[data-gallery-prev]').forEach((button) =>
          button.addEventListener('click', () => this.goTo(this.activeIndex - 1))
        );
        this.querySelectorAll('[data-gallery-next]').forEach((button) =>
          button.addEventListener('click', () => this.goTo(this.activeIndex + 1))
        );
        this.querySelectorAll('[data-gallery-thumb]').forEach((button) =>
          button.addEventListener('click', () => this.goTo(Number(button.dataset.galleryThumb)))
        );
        this.querySelectorAll('[data-gallery-dot]').forEach((button) =>
          button.addEventListener('click', () => this.goTo(Number(button.dataset.galleryDot)))
        );
        this.track.addEventListener('scroll', Theme.debounce(() => this.onScroll(), 80), { passive: true });
        this.track.addEventListener('keydown', (event) => this.onKeydown(event));
        this.addEventListener('click', (event) => {
          const zoom = event.target.closest('[data-zoom-index]');
          if (zoom) this.openZoom(Number(zoom.dataset.zoomIndex), zoom);
        });

        this.unsubscribe = Theme.on(Theme.events.VARIANT_CHANGE, (event) => {
          const { variant, sectionId } = event.detail || {};
          if (sectionId !== this.sectionId || !variant || !variant.featured_media_id) return;
          this.showMedia(variant.featured_media_id);
        });

        if (this.activeIndex > 0) this.goTo(this.activeIndex, false);
        this.setActive(this.activeIndex);
      }

      disconnectedCallback() {
        this.unsubscribe?.();
      }

      get slides() {
        return Array.from(this.track.querySelectorAll(':scope > [data-index]'));
      }

      /** True when the track currently behaves as a horizontal slider. */
      get isSlider() {
        return this.track.scrollWidth > this.track.clientWidth + 2;
      }

      goTo(index, smooth = true) {
        const slides = this.slides;
        if (!slides.length) return;
        const target = (index + slides.length) % slides.length;
        const slide = slides[target];
        if (this.isSlider) {
          this.track.scrollTo({
            left: slide.offsetLeft - this.track.offsetLeft,
            behavior: smooth && !Theme.prefersReducedMotion() ? 'smooth' : 'auto',
          });
        } else if (smooth) {
          slide.scrollIntoView({ behavior: Theme.prefersReducedMotion() ? 'auto' : 'smooth', block: 'nearest' });
        }
        this.setActive(target);
      }

      onScroll() {
        if (!this.isSlider) return;
        const width = this.track.clientWidth || 1;
        const index = Math.round(this.track.scrollLeft / width);
        if (index !== this.activeIndex) this.setActive(index);
      }

      onKeydown(event) {
        if (event.target !== this.track) return;
        if (event.key === 'ArrowRight') {
          event.preventDefault();
          this.goTo(this.activeIndex + 1);
        } else if (event.key === 'ArrowLeft') {
          event.preventDefault();
          this.goTo(this.activeIndex - 1);
        }
      }

      setActive(index) {
        this.activeIndex = index;
        this.slides.forEach((slide, i) => {
          const active = i === index;
          slide.classList.toggle('is-active', active);
          if (!active) this.pauseMedia(slide);
        });
        this.querySelectorAll('[data-gallery-thumb]').forEach((thumb) => {
          const active = Number(thumb.dataset.galleryThumb) === index;
          if (active) {
            thumb.setAttribute('aria-current', 'true');
            const rail = thumb.closest('[data-gallery-thumbs]');
            if (rail && rail.scrollHeight > rail.clientHeight + 2) {
              rail.scrollTo({ top: thumb.offsetTop - rail.clientHeight / 2 + thumb.offsetHeight / 2, behavior: 'smooth' });
            } else if (rail && rail.scrollWidth > rail.clientWidth + 2) {
              rail.scrollTo({ left: thumb.offsetLeft - rail.clientWidth / 2 + thumb.offsetWidth / 2, behavior: 'smooth' });
            }
          } else {
            thumb.removeAttribute('aria-current');
          }
        });
        this.querySelectorAll('[data-gallery-dot]').forEach((dot) => {
          if (Number(dot.dataset.galleryDot) === index) dot.setAttribute('aria-current', 'true');
          else dot.removeAttribute('aria-current');
        });
        const counter = this.querySelector('[data-gallery-current]');
        if (counter) counter.textContent = String(index + 1);
      }

      /** Show the media attached to a variant. */
      showMedia(mediaId) {
        const slide = this.track.querySelector(`[data-media-id="${mediaId}"]`);
        if (!slide) return;
        if (this.dataset.layout === 'stacked' && !this.isSlider) {
          // Stacked desktop grid: bring the variant image to the top.
          this.track.prepend(slide);
          this.slides.forEach((item, i) => {
            item.dataset.index = String(i);
          });
          this.setActive(0);
          return;
        }
        this.goTo(Number(slide.dataset.index));
      }

      pauseMedia(slide) {
        slide.querySelectorAll('video').forEach((video) => video.pause());
        slide.querySelectorAll('iframe').forEach((iframe) => {
          iframe.contentWindow?.postMessage('{"event":"command","func":"pauseVideo","args":""}', '*');
          iframe.contentWindow?.postMessage('{"method":"pause"}', '*');
        });
      }

      openZoom(index, opener) {
        const modal = this.dataset.zoomId && document.getElementById(this.dataset.zoomId);
        if (!modal || typeof modal.open !== 'function') return;
        modal.open(opener);
        const list = modal.querySelector('[data-zoom-list]');
        const item = modal.querySelector(`[data-zoom-item="${index}"]`);
        requestAnimationFrame(() => {
          item?.scrollIntoView({ block: 'start' });
          list?.focus({ preventScroll: true });
        });
        if (list && !list.dataset.bound) {
          list.dataset.bound = 'true';
          list.addEventListener('keydown', (event) => {
            if (!['ArrowDown', 'ArrowUp', 'ArrowRight', 'ArrowLeft'].includes(event.key)) return;
            const items = Array.from(list.querySelectorAll('[data-zoom-item]'));
            const panel = list.closest('[data-panel]') || list;
            const current = items.findIndex((el) => el.getBoundingClientRect().bottom > panel.getBoundingClientRect().top + 40);
            const step = event.key === 'ArrowDown' || event.key === 'ArrowRight' ? 1 : -1;
            const next = items[Math.min(items.length - 1, Math.max(0, current + step))];
            if (next) {
              event.preventDefault();
              next.scrollIntoView({ behavior: Theme.prefersReducedMotion() ? 'auto' : 'smooth', block: 'start' });
            }
          });
        }
      }
    }

    customElements.define('product-gallery', ProductGallery);
  }

  /* --------------------------------------------- <product-deferred-media> */
  if (!customElements.get('product-deferred-media')) {
    class ProductDeferredMedia extends HTMLElement {
      connectedCallback() {
        const button = this.querySelector('[data-deferred-play]');
        if (!button || this.bound) return;
        this.bound = true;
        button.addEventListener('click', () => this.load());
      }

      load() {
        if (this.loaded) return;
        const template = this.querySelector('template[data-deferred-template]');
        if (!template) return;
        this.loaded = true;
        const content = template.content.firstElementChild?.cloneNode(true);
        if (!content) return;
        this.appendChild(content);
        this.classList.add('is-loaded');
        this.querySelector('[data-deferred-play]')?.remove();

        if (content.tagName === 'VIDEO') {
          content.play?.().catch(() => {});
          content.focus?.();
        } else if (this.dataset.mediaType === 'model') {
          loadModelViewer(content);
        } else {
          content.focus?.();
        }
      }
    }

    /** Shopify's 3D model UI + AR (Shopify XR), loaded only when a model is opened. */
    const loadModelViewer = (modelViewer) => {
      if (!window.Shopify || typeof window.Shopify.loadFeatures !== 'function') return;
      window.Shopify.loadFeatures([
        {
          name: 'model-viewer-ui',
          version: '1.0',
          onLoad: (errors) => {
            if (errors || !window.Shopify.ModelViewerUI) return;
            const element = modelViewer.querySelector?.('model-viewer') || modelViewer;
            const ui = new window.Shopify.ModelViewerUI(element);
            ui.play?.();
          },
        },
        {
          name: 'shopify-xr',
          version: '1.0',
          onLoad: (errors) => {
            if (errors || !window.ShopifyXR) return;
            document.querySelectorAll('[data-shopify-xr-hidden]').forEach((el) => el.removeAttribute('data-shopify-xr-hidden'));
            window.ShopifyXR.setupXRElements();
          },
        },
      ]);
    };

    customElements.define('product-deferred-media', ProductDeferredMedia);
  }

  /* ------------------------------------------------ <sticky-add-to-cart> */
  if (!customElements.get('sticky-add-to-cart')) {
    class StickyAddToCart extends HTMLElement {
      connectedCallback() {
        if (this.observer) return;
        const form = document.getElementById(this.dataset.formId);
        const target = form?.querySelector('[data-add-button]') || form;
        if (!target || !('IntersectionObserver' in window)) return;
        this.observer = new IntersectionObserver(
          ([entry]) => {
            // Visible only once the main button has scrolled above the viewport.
            const show = !entry.isIntersecting && entry.boundingClientRect.top < 0;
            this.toggle(show);
          },
          // The root reaches far below the screen, so the only boundary is the top
          // edge: jumps and fast flings past the button are still detected.
          { threshold: 0, rootMargin: '0px 0px 100000px 0px' }
        );
        this.observer.observe(target);
      }

      disconnectedCallback() {
        this.observer?.disconnect();
        this.observer = null;
      }

      toggle(show) {
        this.classList.toggle('is-visible', show);
        this.setAttribute('aria-hidden', String(!show));
        if (show) this.removeAttribute('inert');
        else this.setAttribute('inert', '');
      }
    }

    customElements.define('sticky-add-to-cart', StickyAddToCart);
  }

  /* ------------------------------------------------------ <share-button> */
  if (!customElements.get('share-button')) {
    class ShareButton extends HTMLElement {
      connectedCallback() {
        const trigger = this.querySelector('button');
        if (!trigger || this.bound) return;
        this.bound = true;
        this.status = this.querySelector('[data-share-status]');
        trigger.addEventListener('click', () => this.share());
      }

      async share() {
        const url = this.dataset.url || window.location.href;
        const title = this.dataset.title || document.title;
        if (navigator.share) {
          try {
            await navigator.share({ title, url });
            return;
          } catch (error) {
            if (error.name === 'AbortError') return;
          }
        }
        try {
          await navigator.clipboard.writeText(url);
          this.setStatus(this.dataset.copied);
        } catch (error) {
          // Clipboard blocked: show the URL so it can be copied manually.
          this.setStatus(url);
        }
      }

      setStatus(message) {
        if (!this.status) return;
        this.status.textContent = message;
        clearTimeout(this.timer);
        this.timer = setTimeout(() => {
          this.status.textContent = '';
        }, 4000);
      }
    }

    customElements.define('share-button', ShareButton);
  }
})();
